import axios from "axios";
import type { ApplicationFormData } from "../types/application";
import { translations } from "../i18n/translations";
import type { Language } from "../context/LanguageContext";

// Relative path — Nginx proxies /api/* to the backend and injects the API key server-side.
const API_BASE_URL = "/api";

export interface ApplicationSubmissionData {
  first_name: string;
  last_name: string;
  preferred_email: string;
  citizenship: string;
  current_location: string;
  has_priority_entitlement: boolean;
  currently_federal_employee: boolean;
  current_classification: string;
  current_level: string;
  employment_type: string;
  employment_end_date: string;
  has_pri_number: string;
  qualified_fully_assessed_pool: string;
  staffing_process: string;
  group_fully_assessed: string;
  level_fully_assessed: string;
  first_official_language: string;
  has_valid_sle_results: boolean;
  sle_reading: boolean;
  sle_writing: boolean;
  sle_oral: boolean;
  sle_level: string;
  sle_expiry_date: string;
  interested_english_essential: boolean;
  interested_french_essential: boolean;
  interested_bilingual: boolean;
  security_clearance: string;
  highest_education: string;
  postsecondary_level: string;
  institution_name: string;
  area_of_study: string;
  graduation_date: string;
  seeking_it_role: boolean;
  it_role_other_specify: string;
  programming_languages: string;
  programming_languages_other: string;
  frameworks_libraries: string;
  frameworks_libraries_other: string;
  cloud_platforms: string;
  cloud_platforms_other: string;
  data_analytics_tools: string;
  data_analytics_tools_other: string;
  ai_ml_tools: string;
  ai_ml_tools_other: string;
  cybersecurity_tools: string;
  cybersecurity_tools_other: string;
  devops_tools: string;
  devops_tools_other: string;
  version_control_tools: string;
  version_control_tools_other: string;
  enterprise_tools: string;
  enterprise_tools_other: string;
  project_collaboration_tools: string;
  project_collaboration_tools_other: string;
  project_methodologies: string;
  project_methodologies_other: string;
  consent_to_contact: boolean;
  resume_text: string;
  additional_information: string;
  documentid?: string;
}

export interface ApplicationSubmissionResponse {
  success: boolean;
  message: string;
  application_id?: string;
  errors?: string[];
}

class ApplicationService {
  private api = axios.create({
    baseURL: API_BASE_URL,
    headers: {
      "Content-Type": "application/json",
    },
  });

  /**
   * Validate required fields before submission
   */
  validateApplication(data: ApplicationFormData): {
    isValid: boolean;
    missingFields: string[];
  } {
    const missingFields: string[] = [];
    const enhancedData = data.enhancedData || {};

    // Required fields from database schema
    const requiredFields = [
      { key: "firstName", label: "First Name" },
      { key: "lastName", label: "Last Name" },
      { key: "email", label: "Email Address" },
      { key: "citizenship", label: "Citizenship", enhanced: true },
      { key: "currentLocation", label: "Current Location", enhanced: true },
      {
        key: "firstOfficialLanguage",
        label: "First Official Language",
        enhanced: true,
      },
      {
        key: "highestEducationLevel",
        label: "Highest Education Level",
        enhanced: true,
      },
      { key: "documentId", label: "Resume" },
    ];

    requiredFields.forEach((field) => {
      const value = field.enhanced
        ? enhancedData[field.key as keyof typeof enhancedData]
        : data[field.key as keyof ApplicationFormData];
      if (!value || (typeof value === "string" && value.trim() === "")) {
        missingFields.push(field.label);
      }
    });

    return {
      isValid: missingFields.length === 0,
      missingFields,
    };
  }

  /**
   * Convert frontend ApplicationFormData to backend submission format
   */
  private convertToSubmissionFormat(
    data: ApplicationFormData
  ): ApplicationSubmissionData {
    const enhanced = data.enhancedData || {};

    // Helper function to convert arrays to comma-separated strings
    const arrayToCommaSeparated = (arr: string[] | undefined): string => {
      return arr && arr.length > 0
        ? arr.filter((item) => item !== "Other").join(", ")
        : "";
    };

    const normalizeSelectionText = (value: string) =>
      value.toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();

    const normalizedSelectionSource = (values: string[] | undefined) =>
      (values || [])
        .flatMap((item) => String(item).split(/[,;|]/))
        .map(normalizeSelectionText)
        .join(" ");

    // Helper function to convert SLE results
    const convertSLEResults = (sleResults: string[] | undefined) => {
      const results = normalizedSelectionSource(sleResults);
      return {
        has_valid_sle_results: results.length > 0 && !results.includes("no"),
        sle_reading: results.includes("reading"),
        sle_writing: results.includes("writing"),
        sle_oral: results.includes("oral"),
      };
    };

    // Helper function to convert language preferences
    const convertLanguagePreferences = (preferences: string[] | undefined) => {
      const prefs = normalizedSelectionSource(preferences);
      return {
        interested_english_essential: prefs.includes("english"),
        interested_french_essential: prefs.includes("french"),
        interested_bilingual:
          prefs.includes("bilingual"),
      };
    };

    const sleResults = convertSLEResults(enhanced.validSLEResults);
    const languagePrefs = convertLanguagePreferences(
      enhanced.languageProfilesInterested
    );

    return {
      // Contact Information (Questions 1-3)
      first_name: data.firstName || "",
      last_name: data.lastName || "",
      preferred_email: data.email || "",

      // Citizenship and Location (Questions 4-5)
      citizenship: enhanced.citizenship || "",
      current_location: enhanced.currentLocation || "",

      // Priority Entitlements (Question 6)
      has_priority_entitlement: enhanced.priorityEntitlementStatus === "Yes",

      // Employment Status (Questions 7-11)
      currently_federal_employee: enhanced.currentlyEmployedFPS === "Yes",
      current_level: enhanced.currentLevel || "",
      current_classification: enhanced.currentClassification || "",
      employment_type: enhanced.typeOfEmployment || "",
      employment_end_date: enhanced.employmentEndDate || "",
      has_pri_number: enhanced.priNumber || "",
      qualified_fully_assessed_pool:
        enhanced.qualifiedFullyAssessedPool || "",
      staffing_process: enhanced.staffingProcess || "",
      group_fully_assessed: enhanced.GroupFullyAssessed || "",
      level_fully_assessed: enhanced.LevelFullyAssessed || "",

      // Language Information (Questions 12-16)
      first_official_language: enhanced.firstOfficialLanguage || "",
      ...sleResults,
      sle_level: enhanced.sleLevel || "",
      sle_expiry_date: enhanced.sleExpiryDate || "",
      ...languagePrefs,

      // Security Clearance (Question 17)
      security_clearance: enhanced.validSecurityClearance || "",

      // Education (Questions 18-22)
      highest_education: enhanced.highestEducationLevel || "",
      postsecondary_level: enhanced.postSecondaryEducationLevel || "",
      institution_name: enhanced.postSecondaryInstitution || "",
      area_of_study: enhanced.areaOfStudy || "",
      graduation_date: enhanced.graduationDate || "",

      // IT Field Interest (Question 23)
      seeking_it_role: enhanced.lookingForITRole !== "No",
      it_role_other_specify:
        enhanced.lookingForITRole === "Other"
          ? enhanced.additionalSkills || ""
          : "",

      // Technical Skills (Questions 24-34)
      programming_languages: arrayToCommaSeparated(
        enhanced.programmingLanguages
      ),
      programming_languages_other: enhanced.programmingLanguages_other || "",
      frameworks_libraries: arrayToCommaSeparated(enhanced.frameworks),
      frameworks_libraries_other: enhanced.frameworks_other || "",
      cloud_platforms: arrayToCommaSeparated(enhanced.cloudOpsTools),
      cloud_platforms_other: enhanced.cloudOpsTools_other || "",
      data_analytics_tools: arrayToCommaSeparated(enhanced.dataAnalyticsTools),
      data_analytics_tools_other: enhanced.dataAnalyticsTools_other || "",
      ai_ml_tools: arrayToCommaSeparated(enhanced.aiMlTools),
      ai_ml_tools_other: enhanced.aiMlTools_other || "",
      cybersecurity_tools: arrayToCommaSeparated(enhanced.cybersecurityTools),
      cybersecurity_tools_other: enhanced.cybersecurityTools_other || "",
      devops_tools: arrayToCommaSeparated(enhanced.devOpsTools),
      devops_tools_other: enhanced.devOpsTools_other || "",
      version_control_tools: arrayToCommaSeparated(
        enhanced.versionControlTools
      ),
      version_control_tools_other: enhanced.versionControlTools_other || "",
      enterprise_tools: arrayToCommaSeparated(enhanced.enterpriseTools),
      enterprise_tools_other: enhanced.enterpriseTools_other || "",
      project_collaboration_tools: arrayToCommaSeparated(
        enhanced.projectCollabTools
      ),
      project_collaboration_tools_other:
        enhanced.projectCollabTools_other || "",
      project_methodologies: arrayToCommaSeparated(
        enhanced.projectMgmtMethodologies
      ),
      project_methodologies_other:
        enhanced.projectMgmtMethodologies_other || "",

      // Resume Section (Questions 35-37) and Additional Information
      consent_to_contact: true, // Assuming consent by submitting
      resume_text: enhanced.additionalSkills || "", // Additional skills from Question 35
      additional_information: [
        enhanced.greatestAchievement
          ? `Greatest Achievement: ${enhanced.greatestAchievement}`
          : "",
        enhanced.additionalInformation
          ? `Additional Information: ${enhanced.additionalInformation}`
          : "",
      ]
        .filter(Boolean)
        .join("\n\n"),
      documentid: data.documentId || "",
    };
  }

  /**
   * Submit application to backend
   */
  async submitApplication(
    data: ApplicationFormData,
    language: Language = 'en'
  ): Promise<ApplicationSubmissionResponse> {
    try {
      // Validate application before submission
      const validation = this.validateApplication(data);
      if (!validation.isValid) {
        return {
          success: false,
          message: "Please fill out all required fields before submitting.",
          errors: validation.missingFields,
        };
      }

      // Convert to submission format
      const submissionData = this.convertToSubmissionFormat(data);

      // Make API call
      const response = await this.api.post(
        "/applications/submit",
        submissionData
      );

      return {
        success: true,
        message: "Application submitted successfully!",
        application_id: response.data.application_id,
      };
    } catch (error) {
      console.error("Error submitting application:", error);

      if (axios.isAxiosError(error) && error.response) {
        return {
          success: false,
          message:
            error.response.data.detail ||
            translations[language].submissionErrorMessage,
          errors: error.response.data.errors || [],
        };
      }

      return {
        success: false,
        message: translations[language].networkErrorMessage,
      };
    }
  }
}

export const applicationService = new ApplicationService();
